import { useEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
  User,
  Shield,
  Bell,
  Users,
  UserCheck,
  KeyRound,
  Save,
  RefreshCw,
  AlertCircle,
  Clock,
  ArrowUpCircle,
  GitBranch,
  Mail,
  Building2,
  Zap,
  Webhook,
  MessageSquare,
  Palette,
  Star,
  Globe,
  Phone,
  Share2,
  Sliders,
  FileText,
  ListFilter,
  Send,
  Timer,
  BarChart3,
  LayoutGrid,
  Camera,
  Bot,
  Brain,
  CalendarClock,
  ArrowLeft,
} from 'lucide-react';
import { Button, Card, Spinner, Badge, useToast } from '@centraoneplus/ui';
import {
  CARD,
  CARD_CLASS,
  Input,
  Notice,
  SearchInput,
  Toggle,
  StatusChip,
  Button as KitButton,
} from '@shared/ui/kit';
import {
  SettingsCard,
  SectionHead,
  FieldLabel,
  FILTER_SELECT,
  ReadOnlyValue,
  ToggleRow,
  LoadingBlock,
} from '@components/settings/chrome';
import { useSettingsStore } from '@stores/settingsStore';
import { useAuthStore } from '@stores/authStore';
import { useUnsavedChangesStore } from '@stores/unsavedChangesStore';
import { authService } from '@services/auth.service';
import { fileService, FileCategory } from '@services/file.service';
import { applyUserLocale } from '@/lib/userLocale';
import { UserAvatar } from '@components/UserAvatar';
import { PasswordInput } from '@components/PasswordInput';
import {
  newPasswordFormSchema,
  PASSWORD_REQUIREMENTS_MESSAGE,
  accountProfileSchema,
  toFieldErrors,
} from '@/lib/validation';
import { useTenantContextStore } from '@stores/tenantContextStore';
import SlaSettings from '@components/settings/SlaSettings';
import { ActiveSessionsCard } from '@components/settings/ActiveSessionsCard';
import EscalationHistory from '@components/settings/EscalationHistory';
import PipelineSettings from '@components/settings/PipelineSettings';
import EmailChannelSettings from '@components/settings/EmailChannelSettings';
import OrganizationSettings from '@components/settings/OrganizationSettings';
import { WorkflowAutomation } from '@components/settings/WorkflowAutomation';
import { ScheduledJobs } from '@components/settings/ScheduledJobs';
import SecuritySettingsPage from '@features/security/SecuritySettingsPage';
import { PlanCard } from '@features/plan';
import { OrgBrainSettingsPanel } from '@features/org-brain';
import WebhookManagement from '@components/settings/WebhookManagement';
import CustomerSegmentsBuilder from '@components/settings/CustomerSegmentsBuilder';

import { CannedResponsesManager } from '@components/settings/CannedResponsesManager';
import BrandingSettings from '@components/settings/BrandingSettings';
import CsatSettings from '@components/settings/CsatSettings';
import ApiKeyManagement from '@components/settings/ApiKeyManagement';
import PortalSettings from '@components/settings/PortalSettings';
import ChatWidgetSettings from '@features/chat-widget/ChatWidgetSettings';
import VoiceChannelSettings from '@components/settings/VoiceChannelSettings';
import SocialChannelSettings from '@components/settings/SocialChannelSettings';
import { TenantContextHeader, TenantRequiredGuard } from '@components/settings/TenantContextHeader';
import { TicketTypesSettings } from '@components/settings/TicketTypesSettings';
import { FieldDefinitionsSettings } from '@components/settings/FieldDefinitionsSettings';
import TeamMembersSettings from '@components/settings/TeamMembersSettings';
import TeamsSettings from '@components/settings/TeamsSettings';
import EmailConfigurationSettings from '@components/settings/EmailConfigurationSettings';
import CustomMetricsManager from '@components/settings/CustomMetricsManager';
import DashboardManagement from '@components/settings/DashboardManagement';
import ExtractionSchemasSettings from '@components/settings/ExtractionSchemasSettings';
import BusinessHoursSettings from '@components/settings/BusinessHoursSettings';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import AppearanceSettings from './AppearanceSettings';
import AuditLogsPage from '@features/audit/AuditLogsPage';
import { AdministratorsOnly } from '@components/settings/AdministratorsOnly';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Role hierarchy for visibility checks
const ROLE_HIERARCHY = ['light_agent', 'agent', 'supervisor', 'admin', 'owner'];
function hasMinRole(userRole: string | undefined, minRole: string): boolean {
  if (!userRole) return false;
  return ROLE_HIERARCHY.indexOf(userRole) >= ROLE_HIERARCHY.indexOf(minRole);
}

// Navigation items for settings sidebar
const settingsNavItems = [
  { path: '/settings/account', label: 'Account', icon: User, description: 'Profile and personal info' },
  { path: '/settings/appearance', label: 'Set Theme', icon: Palette, description: 'Application theme and light/dark appearance' },
  { path: '/settings/security', label: 'Security', icon: Shield, description: 'Password and sessions' },
  { path: '/settings/notifications', label: 'Notifications', icon: Bell, description: 'Email and alerts' },
  { path: '/settings/team', label: 'Team', icon: Users, description: 'Users and roles', minRole: 'admin' },
  // Admins only: it opens Organization Settings, where the organization is changed.
  {
    path: '/settings/organization',
    label: 'Organization Settings',
    icon: Building2,
    description: 'Manage your organization: teams, channels, SLA and more',
    minRole: 'admin',
  },
  // The audit log now lives in Organization Settings (`audit-log` below).
];

// Tenant-specific settings navigation (shown when managing a specific tenant)
const tenantSettingsNavItems = [
  // The organization's own record — name, slug, domain, plan. `adminOnly`
  // because it reads and writes the VIEWER's organization (`/tenants/my-organization`),
  // which is only the right record on the tenant route, and only an admin may
  // edit it.
  { path: 'info', label: 'Info', icon: Building2, description: 'Organization name, domain and plan', adminOnly: true },
  { path: 'general', label: 'General', icon: Sliders, description: 'General organization settings' },
  { path: 'team', label: 'Team Members', icon: Users, description: 'Users and invitations', platformOnly: true },
  { path: 'teams', label: 'Teams', icon: UserCheck, description: 'Group agents under a supervisor' },
  { path: 'email-channels', label: 'Email Channels', icon: Mail, description: 'Email configuration' },
  { path: 'email-config', label: 'Email Provider', icon: Send, description: 'Outbound email (SMTP/SendGrid)' },
  { path: 'voice-channels', label: 'Voice Channel', icon: Phone, description: 'Twilio Voice/Flex setup' },
  { path: 'social-channels', label: 'Social Channels', icon: Share2, description: 'WhatsApp, FB, Twitter, etc.' },
  { path: 'ticket-types', label: 'Ticket Types', icon: FileText, description: 'Configure ticket types' },
  { path: 'ai-configuration', label: 'AI Configuration', icon: Bot, description: 'Configure AI features and extraction' },
  { path: 'org-brain', label: 'Organization Brain', icon: Brain, description: 'Knowledge graph & workspace Q&A' },
  { path: 'field-definitions', label: 'Field Definitions', icon: ListFilter, description: 'Custom field library' },
  { path: 'pipelines', label: 'Pipelines', icon: GitBranch, description: 'Ticket workflows' },
  { path: 'automations', label: 'Automations', icon: Zap, description: 'Workflow automation' },
  { path: 'scheduled-jobs', label: 'Scheduled Jobs', icon: Timer, description: 'Cron-based automation' },
  { path: 'canned-responses', label: 'Canned Responses', icon: MessageSquare, description: 'Quick reply templates' },
  { path: 'sla', label: 'SLA', icon: Clock, description: 'SLA policies' },
  // Admin-only: it exposes every escalation across the tenant, including who
  // owned each ticket at the time, which is not an agent's business.
  {
    path: 'sla/escalations',
    label: 'Escalation History',
    icon: ArrowUpCircle,
    description: 'What has escalated, and why',
    minRole: 'admin',
  },
  { path: 'business-hours', label: 'Business Hours', icon: CalendarClock, description: 'Working hours & holidays' },
  { path: 'webhooks', label: 'Webhooks', icon: Webhook, description: 'API integrations' },
  { path: 'segments', label: 'Segments', icon: Users, description: 'Customer segments' },

  { path: 'branding', label: 'Branding', icon: Palette, description: 'White-label settings' },
  { path: 'portal', label: 'Customer Portal', icon: Globe, description: 'Self-service portal' },
  // Only tenant admins can manage it (the BFF and channel-gateway enforce this
  // too), so it is hidden rather than shown and then refused.
  {
    path: 'chat-widget',
    label: 'Chat Widget',
    icon: Bot,
    description: 'Add the AI chatbot to your website',
    adminOnly: true,
  },
  { path: 'csat', label: 'CSAT Surveys', icon: Star, description: 'Customer satisfaction' },
  { path: 'api-keys', label: 'API Keys', icon: KeyRound, description: 'Manage API access' },
  { path: 'custom-metrics', label: 'Custom Metrics', icon: BarChart3, description: 'Organization KPIs' },
  { path: 'dashboard-management', label: 'Dashboard Management', icon: LayoutGrid, description: 'Configure role dashboards' },
  { path: 'enterprise-security', label: 'Enterprise Security', icon: Shield, description: 'IP & SSO settings' },
  // Tenant admins only. `adminOnly` also keeps it off the platform route: the
  // audit endpoints read the viewer's own tenant, not the one being managed.
  { path: 'audit-log', label: 'Audit Log', icon: FileText, description: 'Who did what, and when', adminOnly: true },
];

// Free-text filter for the Organization Settings sidebar. Matches the query
// (case-insensitive, trimmed) against a nav item's label and description. Purely
// presentational — it narrows what the sidebar lists, never the routes or the
// sections themselves. An empty query matches everything.
/**
 * Whether an Organization Settings section is offered to this viewer.
 * Platform-only sections need the platform route; admin-only sections (the
 * chat widget) need a tenant admin managing their own organization.
 */
export function isTenantNavItemAllowed(
  item: { platformOnly?: boolean; adminOnly?: boolean },
  viewer: { isPlatformRoute: boolean; role?: string },
): boolean {
  if (item.platformOnly && !viewer.isPlatformRoute) return false;
  if (item.adminOnly && (viewer.isPlatformRoute || !hasMinRole(viewer.role, 'admin'))) return false;
  return true;
}

export function matchesNavQuery(
  item: { label: string; description: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.label.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q)
  );
}

// Settings Layout with sidebar navigation
function SettingsLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user } = useAuthStore();
  const { loadTenant, currentTenant } = useTenantContextStore();
  const isAdmin = hasMinRole(user?.role, 'admin');
  const tenantId = user?.tenantId;

  // Warn before switching tabs while the open form (e.g. Account) has unsaved
  // edits. If the user cancels, block the navigation; otherwise let it proceed
  // (the discarded form clears the flag on unmount).
  const hasUnsavedChanges = useUnsavedChangesStore((state) => state.isDirty);
  const guardNavigation = (e: React.MouseEvent) => {
    if (
      hasUnsavedChanges &&
      !window.confirm('You have unsaved changes. Leave without saving?')
    ) {
      e.preventDefault();
    }
  };

  // Auto-load tenant context for admin users so tenant settings work
  // Only depend on stable values to prevent infinite re-fetch loops
  useEffect(() => {
    if (isAdmin && tenantId && (!currentTenant || currentTenant.id !== tenantId)) {
      loadTenant(tenantId).catch(() => {});
    }
  }, [isAdmin, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
        <div className="flex h-full flex-col">
      {/* This route's h1 lives in its sidebar, so it has no header row of
          its own — the plan gives it a minimal one rather than bringing
          back a global bar. */}
      <div className="flex-none bg-[var(--co-canvas)] pr-[14px] pt-[14px]">
        <div
          className="flex items-center justify-between gap-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-3"
          style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
        >
          <span className="min-w-0 truncate font-mono text-[11px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
            Settings
          </span>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>
      </div>
      {/* Same shell as the organization settings below it. `/settings/team`
          renders the SAME component under both, so the two rails have to agree
          or that one screen changes appearance depending on how it was reached. */}
      <div className="co-settings-surface flex min-h-0 flex-1 gap-[14px] bg-[var(--co-canvas)] py-[14px] pr-[14px]">
        {/* Section rail */}
        <div
          className={`flex w-[212px] flex-none flex-col overflow-hidden ${CARD_CLASS}`}
          style={CARD}
        >
          <div className="flex-none border-b border-[var(--co-line-subtle)] px-[14px] pb-[13px] pt-[15px]">
            <h1 className="co-mono text-[10px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-3)]">
              Settings
            </h1>
            <p className="mt-[6px] text-[12.5px] leading-[1.4] text-[var(--co-ink-3)]">
              Manage your account and preferences
            </p>
          </div>

          <nav className="co-scrollbar-none min-h-0 flex-1 space-y-[2px] overflow-y-auto p-[10px]">
            {settingsNavItems.map((item) => {
              if (item.minRole && !hasMinRole(user?.role, item.minRole)) return null;
              const isActive = location.pathname === item.path;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={guardNavigation}
                  title={item.description}
                  className={`block truncate rounded-md border-l-2 px-[10px] py-[9px] text-[12.5px] transition-colors ${
                    isActive ? '' : 'hover:bg-[var(--co-selected)]'
                  }`}
                  style={{
                    background: isActive ? 'var(--co-selected)' : 'transparent',
                    borderLeftColor: isActive ? 'var(--co-brass)' : 'transparent',
                    color: isActive ? 'var(--co-ink)' : 'var(--co-ink-2)',
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Content area */}
        <div className="co-scrollbar-none min-w-0 flex-1 overflow-y-auto">
          <div className="max-w-[1440px]">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// Tenant Settings Layout with TenantContextHeader and tenant-specific navigation
interface TenantSettingsLayoutProps {
  children: React.ReactNode;
  exitPath?: string;
  standalone?: boolean;
}

function TenantSettingsLayout({ children, exitPath, standalone = false }: TenantSettingsLayoutProps) {
  const location = useLocation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { currentTenant, loadTenant, isLoading } = useTenantContextStore();
  const { user } = useAuthStore();

  // Free-text search that filters the sidebar sections below. Local UI state
  // only — it doesn't touch routing or the active section.
  const [navQuery, setNavQuery] = useState('');

  // Warn before switching tabs while an open form has unsaved edits (same guard
  // as SettingsLayout). Hook must run before the early loading return below.
  const hasUnsavedChanges = useUnsavedChangesStore((state) => state.isDirty);
  const guardNavigation = (e: React.MouseEvent) => {
    if (
      hasUnsavedChanges &&
      !window.confirm('You have unsaved changes. Leave without saving?')
    ) {
      e.preventDefault();
    }
  };

  // Load tenant if not already loaded or if tenantId changed
  // Only depend on tenantId to prevent infinite re-fetch loops
  useEffect(() => {
    if (tenantId && (!currentTenant || currentTenant.id !== tenantId)) {
      loadTenant(tenantId).catch(console.error);
    }
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Spinner size="lg" />
      </div>
    );
  }

  // Auto-compute basePath from current URL pattern
  const isPlatformRoute = location.pathname.startsWith('/platform/');
  const basePath = isPlatformRoute
    ? `/platform/tenants/${tenantId}/settings`
    : `/settings/tenant/${tenantId}`;
  const resolvedExitPath = exitPath || (isPlatformRoute ? '/platform/tenants' : '/settings/tenants');

  // Sections visible in the sidebar: platform-only entries stay gated to the
  // platform route (unchanged), admin-only entries to tenant admins, then
  // narrowed by the search query.
  const visibleNavItems = tenantSettingsNavItems.filter(
    (item) =>
      isTenantNavItemAllowed(item, { isPlatformRoute, role: user?.role }) && matchesNavQuery(item, navQuery),
  );

  return (
    <TenantRequiredGuard exitPath={resolvedExitPath}>
      <div
        className={`co-settings-surface flex flex-col bg-[var(--co-canvas)] ${
          standalone ? 'h-screen' : 'h-full'
        }`}
      >
        {/* Phase 36 §22 — the shell the sub-pages share: the org band, then a
            212px section rail beside the content column. All three float as
            cards on the canvas rather than as full-bleed panels welded to the
            window edge. The padding lives here, once, so a page with a sticky
            header of its own no longer has content scrolling through a second
            gutter. */}
        <div
          className={`flex min-h-0 flex-1 flex-col gap-[14px] ${
            standalone ? 'p-[14px]' : 'py-[14px] pr-[14px]'
          }`}
        >
          {/* The exit affordance (and its "this will lock the session / back to
              the organizations list" confirmation) is a platform-admin concept:
              only a superadmin manages someone else's org and needs to unlock out
              of it. A tenant admin has exactly one org, and `/settings/tenants`
              isn't even a declared route — clicking through would clear their
              tenant context and land them on the catch-all ErrorPage. So offer it
              only on the platform route; tenant admins leave via the main app
              nav. */}
          <TenantContextHeader
            exitPath={resolvedExitPath}
            showBackButton={isPlatformRoute}
          />

          {/* Rail + content */}
          <div className="flex min-h-0 flex-1 gap-[14px]">
          <div
            className={`flex w-[212px] flex-none flex-col overflow-hidden ${CARD_CLASS}`}
            style={CARD}
          >
            {/* Fixed head + search — stays put while the section list scrolls. */}
            <div className="flex-none border-b border-[var(--co-line-subtle)] px-[14px] pb-[13px] pt-[15px]">
              {/* Back to the personal Settings page (Account etc.). A fixed link,
                  not history.back(): /settings/organization redirects straight
                  back here, so "back" would loop. Platform admins already get
                  the exit button in TenantContextHeader. */}
              {!isPlatformRoute && (
                <Link
                  to="/settings"
                  onClick={guardNavigation}
                  className="-ml-[6px] mb-[10px] inline-flex items-center gap-[6px] rounded-md px-[6px] py-[4px] text-[12.5px] font-medium text-[var(--co-ink-2)] transition-colors hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
                >
                  <ArrowLeft className="h-[14px] w-[14px]" />
                  Back to Settings
                </Link>
              )}
              <h1 className="co-mono text-[10px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-3)]">
                Organization
              </h1>
              <p className="mt-[6px] text-[12.5px] leading-[1.4] text-[var(--co-ink-3)]">
                Configure your organization
              </p>

              {/* Search sections — filters the list below by name/description. */}
              <div className="mt-[11px]">
                <SearchInput
                  value={navQuery}
                  onChange={(e) => setNavQuery(e.target.value)}
                  placeholder="Search settings…"
                  aria-label="Search organization settings"
                />
              </div>
            </div>

            {/* A scrollbar AND a bottom fade: some systems draw overlay scrollbars
                that only appear while scrolling, and 24 items do not fit a laptop
                screen. The bottom padding lets the last item scroll clear of the fade. */}
            <nav className="scrollbar-thin min-h-0 flex-1 space-y-[2px] overflow-y-auto p-[10px] pb-9 [mask-image:linear-gradient(to_bottom,#000_calc(100%-36px),transparent)]">
              {visibleNavItems.length === 0 && (
                <p className="px-[10px] py-2 text-[12.5px] text-[var(--co-ink-3)]">
                  No settings match &ldquo;{navQuery.trim()}&rdquo;
                </p>
              )}
              {visibleNavItems.map((item) => {
                const fullPath = `${basePath}/${item.path}`;
                // `sla` is a path prefix of `sla/escalations`, so a bare prefix
                // test lit both; the parent yields when a nested item matches.
                const nestedItemActive = visibleNavItems.some(
                  (other) =>
                    other.path.startsWith(`${item.path}/`) &&
                    (location.pathname === `${basePath}/${other.path}` ||
                      location.pathname.startsWith(`${basePath}/${other.path}/`)),
                );
                const isActive = !nestedItemActive &&
                  (location.pathname === fullPath ||
                    location.pathname.startsWith(`${fullPath}/`));

                return (
                  <NavLink
                    key={item.path}
                    to={fullPath}
                    end={nestedItemActive}
                    onClick={guardNavigation}
                    /* The one-line description moves to the tooltip rather than
                       disappearing: the search below still matches on it, so a
                       section found by its description can still be recognised. */
                    title={item.description}
                    className={`block truncate rounded-md border-l-2 px-[10px] py-[9px] text-[12.5px] transition-colors ${
                      isActive ? '' : 'hover:bg-[var(--co-selected)]'
                    }`}
                    style={{
                      background: isActive ? 'var(--co-selected)' : 'transparent',
                      borderLeftColor: isActive ? 'var(--co-brass)' : 'transparent',
                      color: isActive ? 'var(--co-ink)' : 'var(--co-ink-2)',
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          {/* Content column. No padding of its own — the shell above supplies
              the gutter on every side. */}
          <div className="co-scrollbar-none min-w-0 flex-1 overflow-y-auto">
            <div className="max-w-[1440px]">
              {children}
            </div>
          </div>
          </div>
        </div>
      </div>
    </TenantRequiredGuard>
  );
}

// General Organization Settings Component (placeholder for basic organization settings)
function TenantGeneralSettings() {
  const { currentTenant } = useTenantContextStore();
  const { user } = useAuthStore();
  const toast = useToast();
  const [features, setFeatures] = useState<Record<string, any>>({});
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [emailSettings, setEmailSettings] = useState<Record<string, any>>({});
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(true);
  const [emailSettingsSaving, setEmailSettingsSaving] = useState(false);

  useEffect(() => {
    if (currentTenant?.id) {
      import('@services/tenant.service').then(({ tenantService }) => {
        // Load features
        tenantService.getFeatures(currentTenant.id).then(f => {
          setFeatures(f || {});
          setFeaturesLoading(false);
        }).catch((err: any) => {
          setFeaturesLoading(false);
          toast.error('Failed to load features', err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.');
        });

        // Load tenant settings for email settings
        tenantService.getTenantSettings(currentTenant.id).then((settings: any) => {
          setEmailSettings(settings?.emailSettings || { ticketConfirmationEnabled: true });
          setEmailSettingsLoading(false);
        }).catch((err: any) => {
          setEmailSettings({ ticketConfirmationEnabled: true });
          setEmailSettingsLoading(false);
          toast.error('Failed to load settings', err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.');
        });
      });
    }
  }, [currentTenant?.id]);

  const handleFeatureToggle = async (key: string, value: boolean) => {
    if (!currentTenant?.id) return;
    setFeaturesSaving(true);
    try {
      const { tenantService } = await import('@services/tenant.service');
      const updated = { ...features, [key]: value };
      await tenantService.updateFeatures(currentTenant.id, updated as any);
      setFeatures(updated);
      toast.success('Feature updated');
    } catch (e: any) {
      console.error('Failed to update feature:', e);
      toast.error('Failed to update feature', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Please try again.');
    } finally {
      setFeaturesSaving(false);
    }
  };

  const handleEmailSettingToggle = async (key: string, value: boolean) => {
    if (!currentTenant?.id) return;
    setEmailSettingsSaving(true);
    try {
      const { tenantService } = await import('@services/tenant.service');
      const updatedEmailSettings = { ...emailSettings, [key]: value };
      await tenantService.updateTenantSettings(currentTenant.id, {
        emailSettings: updatedEmailSettings,
      } as any);
      setEmailSettings(updatedEmailSettings);
      toast.success('Setting updated');
    } catch (e: any) {
      console.error('Failed to update email setting:', e);
      toast.error('Failed to update setting', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Please try again.');
    } finally {
      setEmailSettingsSaving(false);
    }
  };

  if (!currentTenant) {
    return null;
  }

  return (
    <div className="flex flex-col gap-[14px]">
      <SettingsCard>
        <SectionHead
          title="General"
          description={`Basic configuration for ${currentTenant.name}.`}
        />
      </SettingsCard>

      {/* Workspace — identity, as the platform holds it. Name and slug are
          owned by the platform, so they are shown in the shape of the field
          they would be if this screen could change them, and not as bare text. */}
      <SettingsCard>
        <SectionHead
          title="Workspace"
          description="How this organization is identified across the product."
        />

        <div className="mt-4 flex flex-wrap gap-[14px]">
          <div className="min-w-[240px] flex-1">
            <FieldLabel>Organization name</FieldLabel>
            <div className="mt-[7px]">
              <ReadOnlyValue>{currentTenant.name}</ReadOnlyValue>
            </div>
          </div>
          <div className="w-[260px]">
            <FieldLabel>Slug</FieldLabel>
            <div className="mt-[7px]">
              <ReadOnlyValue mono>{currentTenant.slug}</ReadOnlyValue>
            </div>
          </div>
        </div>

        {(currentTenant.plan || currentTenant.status) && (
          <div className="mt-[14px] flex flex-wrap items-start gap-[14px] border-t border-[var(--co-line-subtle)] pt-[14px]">
            {currentTenant.plan && (
              <div className="min-w-[240px] flex-1">
                <FieldLabel>Plan</FieldLabel>
                <div className="mt-[7px] flex items-center gap-[10px]">
                  <StatusChip tone="neutral">{currentTenant.plan}</StatusChip>
                  <span className="text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
                    Changed in Plan &amp; Billing below.
                  </span>
                </div>
              </div>
            )}
            {currentTenant.status && (
              <div className="w-[260px]">
                <FieldLabel>Status</FieldLabel>
                <div className="mt-[7px]">
                  <StatusChip tone={currentTenant.status === 'active' ? 'ok' : 'neutral'}>
                    {currentTenant.status}
                  </StatusChip>
                </div>
              </div>
            )}
          </div>
        )}
      </SettingsCard>

      {/* Plan & Billing — tenant-visible plan change workflow */}
      {currentTenant.id && (
        <SettingsCard>
          <SectionHead
            title="Plan & Billing"
            description="The plan this organization is on, and how to change it."
          />
          <div className="mt-4">
            <PlanCard
              tenantId={currentTenant.id}
              userRole={user?.role ?? ''}
            />
          </div>
        </SettingsCard>
      )}

      {/* Ticket Settings */}
      <SettingsCard>
        <SectionHead
          title="Tickets"
          description="Applies to tickets created from here on. Nothing already open is rewritten, so the audit trail stays true."
        />
        <div className="mt-2">
          {featuresLoading ? (
            <LoadingBlock />
          ) : (
            /* The value it sends is the negation of the state drawn, which is
               what the old inline `!features.allowAgentSelfAssign` failed to do
               while the flag was still unset: undefined renders as ON (the
               default is permissive) but negated to `true`, so the first click
               on a fresh tenant sent the value it already had and the switch
               appeared dead. */
            <ToggleRow
              last
              label="Allow agents to view and self-assign unassigned tickets"
              description="When enabled, agents can see unassigned tickets in their inbox and assign them to themselves. When disabled, only supervisors and admins can see and assign unassigned tickets."
              control={
                <Toggle
                  label="Allow agents to view and self-assign unassigned tickets"
                  on={features.allowAgentSelfAssign !== false}
                  disabled={featuresSaving}
                  onChange={(next) => handleFeatureToggle('allowAgentSelfAssign', next)}
                />
              }
            />
          )}
        </div>
      </SettingsCard>

      {/* Email Notification Settings */}
      <SettingsCard>
        <SectionHead
          title="Email notifications"
          description="What this organization sends to customers automatically."
        />
        <div className="mt-2">
          {emailSettingsLoading ? (
            <LoadingBlock />
          ) : (
            <ToggleRow
              last
              label="Send ticket creation confirmation emails"
              description="When enabled, customers receive an automatic confirmation email when they submit a new ticket. Disable this if you prefer not to send these notifications."
              control={
                <Toggle
                  label="Send ticket creation confirmation emails"
                  on={emailSettings.ticketConfirmationEnabled !== false}
                  disabled={emailSettingsSaving}
                  onChange={(next) =>
                    handleEmailSettingToggle('ticketConfirmationEnabled', next)
                  }
                />
              }
            />
          )}
        </div>
      </SettingsCard>
    </div>
  );
}

// Account Settings Component
// Profile photo upload constraints (validated client-side; on Save the image
// is uploaded via the file service and the returned URL saved as avatarUrl).
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif']; // jpg & jpeg both report image/jpeg
const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB

function AccountSettings() {
  const { profile, isLoadingProfile, isSaving, fetchProfile, updateProfile } = useSettingsStore();
  const updateAuthUser = useAuthStore((state) => state.updateUser);
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    timezone: 'UTC',
    locale: 'en-US',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Field-level validation errors keyed by field name (driven by Zod).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Preview of the newly selected profile photo (object URL) and the file
  // itself, uploaded when the form is saved.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  // If the saved avatar image fails to load, fall back to the initials.
  const [savedAvatarError, setSavedAvatarError] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Revoke the object URL when it is replaced or the component unmounts,
  // so repeated selections don't leak memory.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handlePhotoButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so selecting the same file again still fires onChange.
    e.target.value = '';

    if (!file) {
      toast.error('Please select an image to upload.');
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Only JPG, JPEG, PNG, and GIF files are allowed.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image size must not exceed 2 MB.');
      return;
    }

    // Show the selected image immediately; it is uploaded on Save.
    setSelectedPhoto(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        displayName: profile.displayName || '',
        timezone: (profile.preferences as Record<string, string>)?.timezone || 'UTC',
        locale: (profile.preferences as Record<string, string>)?.locale || 'en-US',
      });
    }
  }, [profile]);

  // Retry showing the saved avatar when its URL changes (e.g. after upload).
  useEffect(() => {
    setSavedAvatarError(false);
  }, [profile?.avatarUrl]);

  // Baseline values from the loaded profile, used for dirty-checking so the
  // Save button only enables when the user actually changes something.
  const baseline = {
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    displayName: profile?.displayName || '',
    timezone: (profile?.preferences as Record<string, string>)?.timezone || 'UTC',
    locale: (profile?.preferences as Record<string, string>)?.locale || 'en-US',
  };
  const isDirty =
    !!profile &&
    (formData.firstName !== baseline.firstName ||
      formData.lastName !== baseline.lastName ||
      formData.displayName !== baseline.displayName ||
      formData.timezone !== baseline.timezone ||
      formData.locale !== baseline.locale ||
      selectedPhoto !== null);

  // Zod drives validity: all user-information fields are mandatory.
  // Save stays disabled while the form is invalid.
  const isFormValid = accountProfileSchema.safeParse(formData).success;

  // Publish dirty state so the settings tab links (in the parent layout) can
  // warn before navigating away, and clear it on unmount.
  const setUnsavedChanges = useUnsavedChangesStore((state) => state.setIsDirty);
  useEffect(() => {
    setUnsavedChanges(isDirty);
    return () => setUnsavedChanges(false);
  }, [isDirty, setUnsavedChanges]);

  // Warn on browser refresh / tab close while there are unsaved edits.
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against duplicate/no-op submits.
    if (!isDirty || isSaving || isUploadingPhoto) return;

    // Validate with Zod before any upload/API call; block + show field errors.
    const validation = accountProfileSchema.safeParse(formData);
    if (!validation.success) {
      setFieldErrors(toFieldErrors(validation.error));
      return;
    }
    setFieldErrors({});

    try {
      // Upload the selected photo first so its URL can be saved on the profile.
      let avatarUrl: string | undefined;
      if (selectedPhoto) {
        setIsUploadingPhoto(true);
        try {
          const uploaded = await fileService.uploadFile(selectedPhoto, {
            category: FileCategory.AVATAR,
            // Avatars must be stored public-read: they render in a plain <img>
            // (and in the header/sidebar) which can't send an auth header, so a
            // privately-stored file's URL would 401 and never display. Without
            // this flag the upload succeeds (2xx) but the photo never appears.
            isPublic: true,
          });
          // Prefer the public blob URL; fall back to the API-served file URL
          // (the BFF always populates `url`) so a missing storageUrl doesn't
          // fail the whole save.
          avatarUrl = uploaded.storageUrl || uploaded.url;
          if (!avatarUrl) {
            throw new Error('Upload did not return an image URL.');
          }
        } finally {
          setIsUploadingPhoto(false);
        }
      }

      await updateProfile({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        displayName: formData.displayName.trim(),
        timezone: formData.timezone,
        locale: formData.locale,
        ...(avatarUrl ? { avatarUrl } : {}),
      });
      // Sync the auth store so the sidebar (name + avatar) updates immediately,
      // without waiting for a refresh. Previously only the avatar was synced, so
      // a name change stayed stale in the chrome until the next reload.
      updateAuthUser({
        name:
          formData.displayName.trim() ||
          `${formData.firstName} ${formData.lastName}`.trim(),
        ...(avatarUrl ? { avatarUrl } : {}),
      });
      if (avatarUrl) {
        setSelectedPhoto(null);
        setPreviewUrl(null);
      }
      // Success: store refreshes `profile`, which resets the dirty baseline.
      // #18930 — reflect the chosen display language on <html lang> right away
      // (and cache it for the next load), until full UI translation ships.
      applyUserLocale(formData.locale);
      toast.success('Profile updated', 'Your account settings have been saved.');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      // Surface the failure instead of swallowing it silently.
      toast.error(
        'Failed to save changes',
        err instanceof Error ? err.message : 'Please try again.',
      );
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-[14px]">
      {/* Pinned to the top of the settings content scroller so Save/Reset stay
          reachable while editing fields further down the page. */}
      <SettingsCard className="sticky top-0 z-10">
        <SectionHead
          title="Account"
          description="Your profile, as the rest of the workspace sees it. Changes apply everywhere your name appears — tickets you own, notes you wrote, and the audit trail."
          actions={
            <>
              <KitButton
                type="button"
                onClick={() => {
                  // Discard the unsaved photo selection along with form edits.
                  setSelectedPhoto(null);
                  setPreviewUrl(null);
                  fetchProfile();
                }}
              >
                <RefreshCw className="h-[15px] w-[15px]" aria-hidden="true" />
                Reset
              </KitButton>
              <KitButton
                variant="primary"
                type="submit"
                disabled={isSaving || isUploadingPhoto || !isDirty || !isFormValid}
                disabledReason={
                  !isSaving && !isUploadingPhoto && isFormValid && !isDirty
                    ? 'No changes to save'
                    : undefined
                }
              >
                {isSaving || isUploadingPhoto ? (
                  <>
                    <Spinner size="sm" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-[15px] w-[15px]" aria-hidden="true" />
                    Save changes
                  </>
                )}
              </KitButton>
            </>
          }
        />
        {saveSuccess && (
          <div className="mt-4">
            <Notice tone="ok" headline="Changes saved" body="Your account settings have been updated." />
          </div>
        )}
      </SettingsCard>

      {/* Profile photo */}
      <SettingsCard>
        <SectionHead
          title="Profile photo"
          description="Shown beside your name in the sidebar, on every ticket you touch, and in the team roster."
        />
        <div className="mt-4 flex items-center gap-4">
          <div
            className="flex h-20 w-20 flex-none items-center justify-center overflow-hidden rounded-[10px]"
            style={{ background: 'var(--co-brass-wash)' }}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Profile photo preview"
                className="h-20 w-20 object-cover"
                // If the selected image fails to load, drop the preview so the
                // initials fallback shows instead of a broken-image icon.
                onError={() => {
                  setPreviewUrl(null);
                  setSelectedPhoto(null);
                }}
              />
            ) : profile?.avatarUrl && !savedAvatarError ? (
              <img
                src={profile.avatarUrl}
                alt="Profile photo"
                className="h-20 w-20 object-cover"
                onError={() => setSavedAvatarError(true)}
              />
            ) : (
              <span
                className="text-[26px] font-semibold"
                style={{ color: 'var(--co-brass-ink)' }}
              >
                {(profile?.firstName?.[0] || profile?.email?.[0] || 'U').toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="co-mono text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              JPG, PNG or GIF · 2 MB max
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif"
              className="hidden"
              onChange={handlePhotoSelected}
            />
            <div className="mt-[10px] flex items-center gap-[10px]">
              <KitButton type="button" onClick={handlePhotoButtonClick}>
                <Camera className="h-[15px] w-[15px]" aria-hidden="true" />
                Change photo
              </KitButton>
              {/* The picked file is not uploaded until Save, so say so — a
                  preview that looks saved but is not is how a photo silently
                  goes missing after a refresh. */}
              {selectedPhoto && (
                <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-terracotta-ink)]">
                  Uploads on save
                </span>
              )}
            </div>
          </div>
        </div>
      </SettingsCard>

      {/* Identity */}
      <SettingsCard>
        <SectionHead title="Your name" description="Required — every one of these fields." />

        <div className="mt-4 flex flex-wrap gap-[14px]">
          <div className="min-w-[240px] flex-1">
            <FieldLabel htmlFor="account-first-name">First name *</FieldLabel>
            <div className="mt-[7px]">
              <Input
                id="account-first-name"
                type="text"
                value={formData.firstName}
                onChange={(e) => {
                  setFormData({ ...formData, firstName: e.target.value });
                  setFieldErrors((prev) => ({ ...prev, firstName: '' }));
                }}
                placeholder="John"
                error={fieldErrors.firstName || undefined}
              />
            </div>
          </div>
          <div className="min-w-[240px] flex-1">
            <FieldLabel htmlFor="account-last-name">Last name *</FieldLabel>
            <div className="mt-[7px]">
              <Input
                id="account-last-name"
                type="text"
                value={formData.lastName}
                onChange={(e) => {
                  setFormData({ ...formData, lastName: e.target.value });
                  setFieldErrors((prev) => ({ ...prev, lastName: '' }));
                }}
                placeholder="Doe"
                error={fieldErrors.lastName || undefined}
              />
            </div>
          </div>
        </div>

        <div className="mt-[14px]">
          <FieldLabel htmlFor="account-display-name">Display name *</FieldLabel>
          <div className="mt-[7px]">
            <Input
              id="account-display-name"
              type="text"
              value={formData.displayName}
              onChange={(e) => {
                setFormData({ ...formData, displayName: e.target.value });
                setFieldErrors((prev) => ({ ...prev, displayName: '' }));
              }}
              placeholder="How you want to be addressed"
              error={fieldErrors.displayName || undefined}
            />
          </div>
          {!fieldErrors.displayName && (
            <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
              This is the name other people see.
            </p>
          )}
        </div>

        {/* Email — owned by an administrator, so it is shown in the shape of the
            field it would be rather than as bare text, and says who can change it. */}
        <div className="mt-[14px] border-t border-[var(--co-line-subtle)] pt-[14px]">
          <FieldLabel>Email address</FieldLabel>
          <div className="mt-[7px]">
            <ReadOnlyValue>{profile?.email || '—'}</ReadOnlyValue>
          </div>
          <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
            Contact your administrator to change your email.
          </p>
        </div>
      </SettingsCard>

      {/* Preferences */}
      <SettingsCard>
        <SectionHead
          title="Regional"
          description="Yours alone. These change how times and text are shown to you and to nobody else — the organization's own zone is set under Organization."
        />

        <div className="mt-4 flex flex-wrap gap-[14px]">
          <div className="min-w-[240px] flex-1">
            <FieldLabel htmlFor="account-timezone">Timezone</FieldLabel>
            <div className="mt-[7px]">
              <ThemedSelect
                id="account-timezone"
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                className={`${FILTER_SELECT} w-full`}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Asia/Singapore">Singapore (SGT)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Australia/Sydney">Sydney (AEST)</option>
              </ThemedSelect>
            </div>
          </div>
          <div className="min-w-[240px] flex-1">
            <FieldLabel htmlFor="account-locale">Language</FieldLabel>
            <div className="mt-[7px]">
              <ThemedSelect
                id="account-locale"
                value={formData.locale}
                onChange={(e) => setFormData({ ...formData, locale: e.target.value })}
                className={`${FILTER_SELECT} w-full`}
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="es-ES">Spanish</option>
                <option value="fr-FR">French</option>
                <option value="de-DE">German</option>
                <option value="ja-JP">Japanese</option>
                <option value="zh-CN">Chinese (Simplified)</option>
              </ThemedSelect>
            </div>
            <p className="mt-[7px] text-xs text-gray-500 dark:text-gray-400">
              Sets your account's document language (used by screen readers) and
              is saved to your profile. The interface is currently shown in
              English only — full in-app translation is coming soon.
            </p>
          </div>
        </div>
      </SettingsCard>
    </form>
  );
}

// Security Settings Component
function SecuritySettings() {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const toast = useToast();

  const resetPasswordForm = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (isChangingPassword) return;

    // Zod is the single source of truth for the new-password policy + match.
    const result = newPasswordFormSchema.safeParse({
      password: passwordData.newPassword,
      confirmPassword: passwordData.confirmPassword,
    });
    if (!result.success) {
      const message = result.error.issues[0]?.message ?? PASSWORD_REQUIREMENTS_MESSAGE;
      setPasswordError(message);
      toast.error('Password change failed', message);
      return;
    }

    setIsChangingPassword(true);
    try {
      await authService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
      );
      setPasswordSuccess(true);
      setShowPasswordForm(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated', 'Your password has been changed successfully.');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: { message?: string } }>;
      let message: string;
      if (axiosError.response?.status === 400) {
        // BFF/auth-service reject an incorrect current password or a new
        // password that fails complexity rules with 400.
        message =
          axiosError.response.data?.error?.message ||
          'Current password is incorrect or the new password does not meet requirements.';
      } else if (axiosError.response?.data?.error?.message) {
        message = axiosError.response.data.error.message;
      } else {
        message = 'Failed to change password. Please try again.';
      }
      // Show the reason both inline and as a toast so the user always gets
      // clear feedback (mirrors the success toast).
      setPasswordError(message);
      toast.error('Password change failed', message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Security Settings</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage your password and authentication</p>
      </div>

      {/* Password Section */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <KeyRound className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Password</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {passwordSuccess
                ? 'Your password has been updated successfully'
                : 'Choose a strong password to secure your account'}
            </p>

            {!showPasswordForm ? (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setShowPasswordForm(true)}
              >
                Change Password
              </Button>
            ) : (
              <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
                {passwordError && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 p-3 rounded-lg">
                    <AlertCircle className="h-4 w-4" />
                    {passwordError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Current Password
                  </label>
                  <PasswordInput
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, currentPassword: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoComplete="current-password"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New Password
                  </label>
                  <PasswordInput
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoComplete="new-password"
                    required
                    minLength={12}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    At least 12 characters with uppercase, lowercase, number, and special character
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm New Password
                  </label>
                  <PasswordInput
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Button type="submit" size="sm" disabled={isChangingPassword}>
                    {isChangingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isChangingPassword}
                    onClick={() => {
                      setShowPasswordForm(false);
                      resetPasswordForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Card>

      <ActiveSessionsCard />
    </div>
  );
}

// Notification Settings Component
const DEFAULT_NOTIFICATION_PREFS = {
  emailEnabled: true,
  inAppEnabled: true,
  pushEnabled: false,
  notifyOnAssignment: true,
  notifyOnReply: true,
  notifyOnMention: true,
  notifyOnSlaBreach: true,
  notifyOnEscalation: true,
  notifyOnSlaEscalation: true,
  dailyDigest: false,
  weeklySummary: true,
  quietHoursStart: '',
  quietHoursEnd: '',
};

function NotificationSettings() {
  const {
    notificationPreferences,
    isSaving,
    fetchNotificationPreferences,
    updateNotificationPreferences,
  } = useSettingsStore();
  const toast = useToast();

  const [prefs, setPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);
  // Baseline of the last loaded/saved values, used to detect unsaved changes
  // so the Save button stays disabled until the user actually edits something.
  const [savedPrefs, setSavedPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);

  const isDirty = JSON.stringify(prefs) !== JSON.stringify(savedPrefs);

  useEffect(() => {
    fetchNotificationPreferences();
  }, [fetchNotificationPreferences]);

  useEffect(() => {
    if (notificationPreferences) {
      const merged = { ...DEFAULT_NOTIFICATION_PREFS, ...notificationPreferences };
      setPrefs(merged);
      setSavedPrefs(merged);
    }
  }, [notificationPreferences]);

  const handleToggle = (key: keyof typeof prefs) => {
    setPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    try {
      await updateNotificationPreferences(prefs);
      setSavedPrefs(prefs);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save preferences'
      );
    }
  };

  const ToggleSwitch = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: () => void;
  }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        checked ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Preferences</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Choose how you want to receive notifications</p>
      </div>

      {/* Channels */}
      <Card className="p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Notification Channels</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Notifications</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Receive notifications via email</p>
            </div>
            <ToggleSwitch
              checked={prefs.emailEnabled}
              onChange={() => handleToggle('emailEnabled')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">In-App Notifications</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">See notifications in the app</p>
            </div>
            <ToggleSwitch
              checked={prefs.inAppEnabled}
              onChange={() => handleToggle('inAppEnabled')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Push Notifications</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Receive browser push notifications</p>
            </div>
            <ToggleSwitch
              checked={prefs.pushEnabled}
              onChange={() => handleToggle('pushEnabled')}
            />
          </div>
        </div>
      </Card>

      {/* Event Types */}
      <Card className="p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Notification Events</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Ticket Assignment</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">When a ticket is assigned to you</p>
            </div>
            <ToggleSwitch
              checked={prefs.notifyOnAssignment}
              onChange={() => handleToggle('notifyOnAssignment')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">New Replies</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">When customers reply to your tickets</p>
            </div>
            <ToggleSwitch
              checked={prefs.notifyOnReply}
              onChange={() => handleToggle('notifyOnReply')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Mentions</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">When someone mentions you in a comment</p>
            </div>
            <ToggleSwitch
              checked={prefs.notifyOnMention}
              onChange={() => handleToggle('notifyOnMention')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">SLA Breaches</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">When tickets are at risk of missing SLA</p>
            </div>
            <ToggleSwitch
              checked={prefs.notifyOnSlaBreach}
              onChange={() => handleToggle('notifyOnSlaBreach')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Escalations</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">When a ticket is escalated by souring customer sentiment</p>
            </div>
            <ToggleSwitch
              checked={prefs.notifyOnEscalation}
              onChange={() => handleToggle('notifyOnEscalation')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">SLA Escalation</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">When an SLA escalation rule pages you (approaching or breached SLA)</p>
            </div>
            <ToggleSwitch
              checked={prefs.notifyOnSlaEscalation}
              onChange={() => handleToggle('notifyOnSlaEscalation')}
            />
          </div>
        </div>
      </Card>

      {/* Digests */}
      <Card className="p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Email Digests</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily Digest</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Summary of daily activity</p>
            </div>
            <ToggleSwitch
              checked={prefs.dailyDigest}
              onChange={() => handleToggle('dailyDigest')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Weekly Summary</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Weekly performance report</p>
            </div>
            <ToggleSwitch
              checked={prefs.weeklySummary}
              onChange={() => handleToggle('weeklySummary')}
            />
          </div>
        </div>
      </Card>

      {/* Quiet Hours */}
      <Card className="p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Quiet Hours</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Pause non-urgent notifications during specific hours
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={prefs.quietHoursStart}
              onChange={(e) => setPrefs({ ...prefs, quietHoursStart: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Time
            </label>
            <input
              type="time"
              value={prefs.quietHoursEnd}
              onChange={(e) => setPrefs({ ...prefs, quietHoursEnd: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex items-center justify-end gap-3">
        {isDirty && !isSaving && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            You have unsaved changes
          </span>
        )}
        <Button onClick={handleSave} disabled={isSaving || !isDirty}>
          {isSaving ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Preferences
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// Team Settings Component (for admins)
function TeamSettings() {
  const {
    users,
    roles,
    isLoading,
    isLoadingRoles,
    total,
    page,
    totalPages,
    fetchUsers,
    fetchRoles,
    activateUser,
    deactivateUser,
    setPage,
  } = useSettingsStore();
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    if (currentStatus === 'active') {
      await deactivateUser(userId);
    } else {
      await activateUser(userId);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">Active</Badge>;
      case 'inactive':
        return <Badge variant="default">Inactive</Badge>;
      case 'suspended':
        return <Badge variant="error">Suspended</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="info">Admin</Badge>;
      case 'supervisor':
        return <Badge variant="warning">Supervisor</Badge>;
      case 'agent':
        return <Badge variant="default">Agent</Badge>;
      default:
        return <Badge variant="default">{role}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Team Management</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage users and roles</p>
        </div>
        <Button>
          <User className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {(['users', 'roles'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <UserAvatar
                              src={user.avatarUrl}
                              name={user.firstName || user.email}
                              className="h-8 w-8 shrink-0 bg-primary-100 dark:bg-primary-900/30 text-sm font-medium text-primary-600 dark:text-primary-400"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(user.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleUserStatus(user.id, user.status)}
                            >
                              {user.status === 'active' ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button variant="ghost" size="sm">
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total} users
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <Card>
          {isLoadingRoles ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {roles.map((role) => (
                <div key={role.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{role.name}</h4>
                        {role.isSystem && (
                          <Badge variant="default">System</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {role.description || 'No description'}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {role.permissions.slice(0, 5).map((permission) => (
                          <span
                            key={permission}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          >
                            {permission}
                          </span>
                        ))}
                        {role.permissions.length > 5 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            +{role.permissions.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" disabled={role.isSystem}>
                        Edit
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * Whether this viewer may open the admin-managed settings (Organization
 * Settings and Team). Platform admins manage tenants from the platform route
 * and are always allowed; inside a tenant it takes an admin or owner.
 *
 * The menus already hid these from everyone else, but the routes themselves
 * were open: a supervisor or agent who typed the address got every section
 * with its create, edit and delete controls.
 */
export function canManageOrganization(viewer: {
  role?: string;
  isPlatformUser?: boolean;
  isPlatformRoute?: boolean;
}): boolean {
  // Lower-cased so a role stored as "Admin" is never locked out of its own settings.
  return !!viewer.isPlatformRoute || !!viewer.isPlatformUser || hasMinRole(viewer.role?.toLowerCase(), 'admin');
}

// Tenant Settings Page with Routes
export function TenantSettingsPage({ standalone, exitPath }: { standalone?: boolean; exitPath?: string } = {}) {
  const location = useLocation();
  const { user } = useAuthStore();
  const isPlatformRoute = location.pathname.startsWith('/platform/');

  if (!canManageOrganization({ role: user?.role, isPlatformUser: user?.isPlatformUser, isPlatformRoute })) {
    return (
      <SettingsLayout>
        <AdministratorsOnly what="change your organization's settings" />
      </SettingsLayout>
    );
  }

  return (
    <TenantSettingsLayout standalone={standalone} exitPath={exitPath}>
      <Routes>
        <Route index element={<Navigate to="general" replace />} />
        {/* Organization identity. Tenant route only: it resolves the viewer's
            own organization, so on the platform route it would show the
            superadmin's org rather than the tenant being managed. */}
        {!isPlatformRoute && <Route path="info" element={<OrganizationSettings embedded />} />}
        <Route path="general" element={<TenantGeneralSettings />} />
        <Route path="email-channels" element={<EmailChannelSettings />} />
        <Route path="email-config" element={<EmailConfigurationSettings />} />
        <Route path="voice-channels" element={<VoiceChannelSettings />} />
        <Route path="social-channels" element={<SocialChannelSettings />} />
        <Route path="ticket-types" element={<TicketTypesSettings />} />
        <Route path="ai-configuration" element={<ExtractionSchemasSettings />} />
        <Route path="org-brain" element={<OrgBrainSettingsPanel />} />
        <Route path="field-definitions" element={<FieldDefinitionsSettings />} />
        <Route path="pipelines" element={<PipelineSettings />} />
        <Route path="automations" element={<WorkflowAutomation />} />
        <Route path="scheduled-jobs" element={<ScheduledJobs />} />
        <Route path="canned-responses" element={<CannedResponsesManager />} />
        <Route path="sla" element={<SlaSettings />} />
        <Route path="sla/escalations" element={<EscalationHistory />} />
        <Route path="business-hours" element={<BusinessHoursSettings />} />
        <Route path="webhooks" element={<WebhookManagement />} />
        <Route path="segments" element={<CustomerSegmentsBuilder />} />

        <Route path="branding" element={<BrandingSettings />} />
        <Route path="portal" element={<PortalSettings />} />
        <Route path="chat-widget" element={<ChatWidgetSettings />} />
        <Route path="csat" element={<CsatSettings />} />
        <Route path="api-keys" element={<ApiKeyManagement />} />
        <Route path="api-keys/guide" element={<ApiKeyManagement />} />
        <Route path="custom-metrics" element={<CustomMetricsManager />} />
        <Route path="dashboard-management" element={<DashboardManagement />} />
        <Route path="enterprise-security" element={<SecuritySettingsPage />} />
        {!isPlatformRoute && <Route path="audit-log" element={<AuditLogSection />} />}
        {/* Team Members only available in org settings for platform/super admin */}
        {isPlatformRoute && <Route path="team" element={<TeamMembersSettings />} />}
        <Route path="teams" element={<TeamsSettings />} />
      </Routes>
    </TenantSettingsLayout>
  );
}

/**
 * Where the Settings sidebar's `Organization` entry should land.
 *
 * An admin manages the organization, so they go straight to Organization
 * Settings (its `Info` section is the detail screen that used to live here) —
 * one click instead of landing on a read-only card and pressing a second
 * button. Everyone else who can see the section (supervisors) cannot manage
 * the organization, so they keep the standalone read-only view rather than
 * being handed a settings shell full of sections they may not change.
 *
 * Returns null when the standalone view is the right destination.
 */
export function organizationSectionTarget(viewer: {
  role?: string;
  tenantId?: string;
}): string | null {
  if (!hasMinRole(viewer.role, 'admin') || !viewer.tenantId) return null;
  return `/settings/tenant/${viewer.tenantId}/info`;
}

/** `/settings/team` — the menu entry is admin-only, and so is the page. */
function TeamSection() {
  const { user } = useAuthStore();
  if (!canManageOrganization({ role: user?.role, isPlatformUser: user?.isPlatformUser })) {
    return <AdministratorsOnly what="manage team members and invitations" />;
  }
  return <TeamMembersSettings />;
}

/**
 * Where the old `/audit-logs` address now goes: the Audit Log section of the
 * viewer's Organization Settings. Without a tenant there is no such section,
 * so it falls back to Settings.
 */
export function auditLogTarget(viewer: { tenantId?: string }): string {
  return viewer.tenantId ? `/settings/tenant/${viewer.tenantId}/audit-log` : '/settings';
}

/** Redirect for bookmarks of the old full-page `/audit-logs` route. */
export function AuditLogsRedirect() {
  const { user } = useAuthStore();
  return <Navigate to={auditLogTarget({ tenantId: user?.tenantId })} replace />;
}

/** Organization Settings → Audit Log. Admin-only, matching its menu entry. */
function AuditLogSection() {
  const { user } = useAuthStore();
  if (!hasMinRole(user?.role?.toLowerCase(), 'admin')) {
    return <AdministratorsOnly what="view the audit log" />;
  }
  return <AuditLogsPage embedded />;
}

function OrganizationSection() {
  const { user } = useAuthStore();
  // Admins only, same as the menu entry: a non-admin who types the address
  // gets the same no-access card as every other admin-only settings page.
  if (!hasMinRole(user?.role, 'admin')) {
    return <AdministratorsOnly what="view or change your organization's settings" />;
  }
  const target = organizationSectionTarget({ role: user?.role, tenantId: user?.tenantId });
  if (target) return <Navigate to={target} replace />;
  return <OrganizationSettings />;
}

// Main Settings Page with Routes
export default function SettingsPage() {
  return (
    <Routes>
      {/* Tenant-specific settings routes */}
      <Route path="tenant/:tenantId/*" element={<TenantSettingsPage />} />

      {/* Global settings routes wrapped in SettingsLayout */}
      <Route
        path="*"
        element={
          <SettingsLayout>
            <Routes>
              <Route index element={<Navigate to="/settings/account" replace />} />
              <Route path="account" element={<AccountSettings />} />
              <Route path="appearance" element={<AppearanceSettings />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="notifications" element={<NotificationSettings />} />
              <Route path="team" element={<TeamSection />} />
              <Route path="organization" element={<OrganizationSection />} />
            </Routes>
          </SettingsLayout>
        }
      />
    </Routes>
  );
}
